import _ from 'underscore';
import appConfig from '@bingads-webui-component-legacy/config/index';
import headerTemplate from '@bingads-webui-component-legacy/bingads-grid/template/header.pug';

const permissions = appConfig.get('Permissions') || {};

/**
 * Abstracts a grid column configuration for projection grid.
 *
 * See {@link https://microsoft.github.io/projection-grid/} for description of the fields provided
 * by the getters and setters here.
 */
class BaseGridColumn {
  /**
   * Constructs a GridColumn object.
   */
  constructor() {
    this.model = {
      // This must use an arrow function in order to bind 'this'
      template: (local) => {
        return this.template(local.value);
      },
    };
  }

  /**
   * Method Can be override by child class to provide a template for the grid cell.
   * If subclass already set formatter, then this is not required
   *
   * @returns {String} the template HTML text.
   */
  template() {
    throw new Error('Error: a subclass of GridColumn must implement the abstract template method.');
  }

  setName(name) {
    this.model.name = name;
  }

  get name() {
    return this.model.name;
  }

  setTitle(title) {
    this.model.title = title;
    if (!_.has(this.model.headAttributes, 'title')) {
      this.setTooltip(title);
    }
  }

  get title() {
    return this.model.title;
  }

  setSortable(sortable) {
    this.model.sortable = sortable;
  }

  setEditable(editable) {
    if (permissions.IsReadOnlyUser) {
      return;
    }

    this.model.editable = editable;
  }

  setBodyClasses(classes) {
    this.model.bodyClasses = classes;
  }

  setHeadClasses(classes) {
    this.model.headClasses = classes;
  }

  setField(field) {
    this.model.field = field;
  }

  setHelpId(helpId) {
    this.model.helpId = helpId;
  }

  setType(type) {
    this.model.type = type;
  }

  /**
   * Method sets column property. By default, the whole model is passed
   * back from property.get()
   *
   * @param {Array} properties List of properties to expose to plugins
   * @returns {void}
   */
  setProperties(properties) {
    this.model.property = {
      get(model) {
        return model;
      },
      key: properties,
    };
  }

  setHeaderHtml(headerHtml) {
    this.model.html = headerHtml;
  }

  setTooltip(tooltip) {
    this.model.headAttributes = _.defaults({
      title: tooltip,
    }, this.model.headAttributes);
  }

  getConfig() {
    if (_.isEmpty(this.model.html) && !_.isEmpty(this.model.helpId)) {
      this.model.html = headerTemplate(this.model); // udpate header to support popupHelp
    }

    return this.model;
  }

  toJSON() {
    return this.getConfig();
  }

  /**
   * Make a grid columns config from a list of column classes.
   *
   * @param {Array} columnClasses An array of column classes.
   * @param {Object} view Has a grid created by pgrid.factory().create()
   * @returns {Array} a grid columns config array.
   */
  static newColumnsConfig(columnClasses, view) {
    const columnsConfig = _.map(columnClasses, (Column) => new Column(view).getConfig());

    return columnsConfig;
  }

  /**
   * Make a list of column names from a list of column classes.
   *
   * @param {Array} columnClasses An array of column classes.
   * @returns {Array} an array of column names.
   */
  static getColumnNames(columnClasses) {
    const columnsConfig = BaseGridColumn.newColumnsConfig(columnClasses);

    return _.map(columnsConfig, (columnConfig) => columnConfig.name);
  }
}

export { BaseGridColumn };
